import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { EMPLOYEE_ROUTES } from '../../../../../common/constants/employee.routing.constants';
import { Meta } from '../../../../../common/models/api-response.model';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { OptionItem } from '../../../../../../ui-components/lib/option.interface';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ReflectionRunStatusEnum } from '../enums/reflection-run-status.enum';
import { ReflectionRun } from '../reflections.models';
import { ReflectionsService } from '../reflections.service';

const CHIP_BASE = 'px-sm border-none text-sm font-medium';

/** Chip colours per run status (success, error, warning, neutral). */
const STATUS_CLASS: Record<ReflectionRunStatusEnum, string> = {
  [ReflectionRunStatusEnum.PENDING]: `${CHIP_BASE} bg-neutral-100 text-neutral-700`,
  [ReflectionRunStatusEnum.RUNNING]: `${CHIP_BASE} bg-warning-50 text-warning-700`,
  [ReflectionRunStatusEnum.SUCCEEDED]: `${CHIP_BASE} bg-success-50 text-success-700`,
  [ReflectionRunStatusEnum.FAILED]: `${CHIP_BASE} bg-destructive-50 text-destructive-700`,
  [ReflectionRunStatusEnum.SKIPPED]: `${CHIP_BASE} bg-neutral-100 text-neutral-700`,
};

const FALLBACK_CLASS = STATUS_CLASS[ReflectionRunStatusEnum.PENDING];

/** The "no filter" entry of the status select. */
const ALL_STATUSES = '';

const statusClass = (status: string): string =>
  STATUS_CLASS[status as ReflectionRunStatusEnum] ?? FALLBACK_CLASS;

/** One attempt of an expanded run, with what the template needs to render it. */
interface AttemptView {
  attempt: number;
  statusKey: string;
  statusClass: string;
  startedAt: string | null;
  durationMs: number | null;
  error: string | null;
  responseStatus: number | null;
}

/** One run with what the template needs to render it. */
interface RunRowView {
  id: number;
  event: string;
  statusKey: string;
  statusClass: string;
  skipReasonKey: string | null;
  error: string | null;
  requestNumber: string;
  requestLink: string;
  createdAt: string | null;
  attemptCount: number;
  expandable: boolean;
  expanded: boolean;
  attempts: AttemptView[];
}

/** Paginated runs of one reflection, filterable by status; a row expands to its attempts. */
@Component({
  selector: 'lib-reflection-run-history',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    TranslocoPipe,
    BadgeComponent,
    ButtonComponent,
    PaginatorComponent,
    SelectComponent,
  ],
  templateUrl: './reflection-run-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionRunHistoryComponent {
  readonly service = inject(ReflectionsService);
  private readonly transloco = inject(TranslocoService);
  private readonly tenant = inject(TenantService);

  flowTemplateId = input.required<number>();
  reflectionId = input.required<number>();

  readonly page = signal(1);
  /** `null` shows every status. */
  readonly status = signal<ReflectionRunStatusEnum | null>(null);
  readonly runs = signal<ReflectionRun[]>([]);
  readonly meta = signal<Meta | null>(null);
  readonly expandedId = signal<number | null>(null);
  readonly loading = signal(true);
  /** The request failed; kept apart from "no runs" so an error never shows the empty state. */
  readonly failed = signal(false);

  /** Throwaway control of the status filter; its changes drive `setStatus`. */
  readonly filterForm = new FormGroup({
    status: new FormControl<string>(ALL_STATUSES, { nonNullable: true }),
  });

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  readonly statusOptions = computed<OptionItem[]>(() => {
    this.lang();
    return [
      {
        value: ALL_STATUSES,
        label: this.transloco.translate('reflection_runs_status_all'),
      },
      ...Object.values(ReflectionRunStatusEnum).map(status => ({
        value: status,
        label: this.transloco.translate(`reflection_run_status_${status}`),
      })),
    ];
  });

  readonly totalPages = computed(() => this.meta()?.last_page ?? 1);
  readonly empty = computed(
    () => !this.loading() && !this.failed() && !this.runs().length
  );

  /** Event labels of the lookup (the lookups are loaded by the list and shared). */
  private readonly eventLabels = computed(() => {
    const labels = new Map<string, string>();
    for (const events of Object.values(this.service.lookups()?.events ?? {})) {
      for (const event of events) {
        labels.set(event.value, event.label);
      }
    }
    return labels;
  });

  readonly rows = computed<RunRowView[]>(() => {
    const labels = this.eventLabels();
    const expandedId = this.expandedId();
    return this.runs().map(run => ({
      id: run.id,
      event: labels.get(run.event) || run.event,
      statusKey: `reflection_run_status_${run.status}`,
      statusClass: statusClass(run.status),
      skipReasonKey: run.skip_reason
        ? `reflection_skip_reason_${run.skip_reason}`
        : null,
      error: run.error,
      requestNumber: run.flow.request_number ?? `#${run.flow.id}`,
      requestLink: this.tenant.tenantPath(
        `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}/${run.flow.id}`
      ),
      createdAt: run.created_at,
      attemptCount: run.attempts.length,
      expandable: run.attempts.length > 0,
      expanded: expandedId === run.id,
      attempts: run.attempts.map(attempt => ({
        attempt: attempt.attempt,
        statusKey: `reflection_run_status_${attempt.status}`,
        statusClass: statusClass(attempt.status),
        startedAt: attempt.started_at,
        durationMs: attempt.duration_ms,
        error: attempt.error ?? null,
        responseStatus: attempt.response_status ?? null,
      })),
    }));
  });

  constructor() {
    toObservable(
      computed(() => ({
        templateId: this.flowTemplateId(),
        id: this.reflectionId(),
        page: this.page(),
        status: this.status(),
      }))
    )
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
        }),
        switchMap(({ templateId, id, page, status }) =>
          this.service.runs(templateId, id, page, status ?? undefined).pipe(
            map(response => ({ response })),
            catchError(() => of(null))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(result => {
        this.loading.set(false);
        if (!result) {
          this.failed.set(true);
          this.runs.set([]);
          this.meta.set(null);
          return;
        }
        this.runs.set(result.response.data ?? []);
        this.meta.set(result.response.meta ?? null);
      });

    this.filterForm.controls.status.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value =>
        this.setStatus(value ? (value as ReflectionRunStatusEnum) : null)
      );
  }

  toggle(id: number): void {
    this.expandedId.update(current => (current === id ? null : id));
  }

  setStatus(status: ReflectionRunStatusEnum | null): void {
    this.filterForm.controls.status.setValue(status ?? ALL_STATUSES, {
      emitEvent: false,
    });
    if (status === this.status()) return;
    this.status.set(status);
    this.page.set(1);
    this.expandedId.set(null);
  }

  goTo(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.page()) return;
    this.page.set(page);
    this.expandedId.set(null);
  }
}
